import { useQuery } from '@tanstack/react-query';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { getPods } from '@/react/kubernetes/applications/queries/getPods';
import { GenericResource } from '@/react/kubernetes/helm/types';

// Refreshed so pod churn (restarts create new pod names) is picked up while the
// logs tab is open.
const POD_REFRESH_INTERVAL = 10000;

/**
 * useReleasePods lists the pods belonging to a helm release, scoped to the same
 * pods shown in the release's resources view (the kind === 'Pod' entries of
 * release.info.resources). getPods provides the full pod objects — including
 * their containers — that the resources list does not carry.
 */
export function useReleasePods(
  environmentId: EnvironmentId,
  namespace: string,
  releaseResources: GenericResource[],
  // allPods returns every pod in the namespace (not just the release's) — used
  // when no release exists to scope against.
  { enabled = true, allPods = false } = {}
) {
  const podNames = releaseResources
    .filter((resource) => resource.kind === 'Pod')
    .map((resource) => resource.metadata.name);

  return useQuery(
    [
      'helm-release-pods',
      environmentId,
      namespace,
      allPods ? 'all' : [...podNames].sort(),
    ],
    async () => {
      const pods = await getPods(environmentId, namespace);
      if (allPods) {
        return pods;
      }
      const names = new Set(podNames);
      return pods.filter(
        (pod) => !!pod.metadata?.name && names.has(pod.metadata.name)
      );
    },
    {
      // No error toast: the logs tab renders its own empty/error states, and
      // this polls, so a failure would otherwise notify on every interval.
      enabled: enabled && !!environmentId && !!namespace,
      // stop polling once it errors, so a failure isn't re-requested every interval
      refetchInterval: (_data, query) =>
        query.state.status === 'error' ? false : POD_REFRESH_INTERVAL,
    }
  );
}
